/* TODO fix by reviewing new combo box arg type */

import React, { ReactNode, useEffect, useState } from 'react';
import {
  autoPlacement,
  autoUpdate,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { Combobox, ComboboxButton, ComboboxOptions } from '@headlessui/react';
import { SkeletonLoader } from '../skeleton-loader';
import { allowedPlacements } from './allowed-placements';
import { DisplayButton } from './DisplayButton';
import { OptionList } from './OptionList';
import { SearchInput } from './SearchInput';
import { SelectAll } from './SelectAll';
import { VirtualOptionList } from './VirtualOptionList';

import { useTranslation } from 'react-i18next';

export type Item = {
  value: string;
  disabled?: boolean;
  label?: ReactNode;
  permanent?: boolean;
  searchString?: string;
  selectedDisplay?: ReactNode;
  /** In multiple mode, picking it clears the rest, and picking another clears it. */
  exclusive?: boolean;
};

interface SingleSelection {
  value?: string;
  setValue: (value: string) => void;
  mode: 'single';
}

interface MultipleSelection {
  value: string[];
  setValue: (value: string[]) => void;
  mode?: 'multiple';
}

/**
 * The picks once an exclusive option has had its way: picking one keeps it
 * alone, and picking any other option drops it.
 */
export function applyExclusive(
  next: string[],
  previous: string[],
  options: Item[],
): string[] {
  const added = next.find((v) => !previous.includes(v));
  if (added === undefined) return next;
  const isExclusive = (v: string) =>
    options.some((o) => o.value === v && o.exclusive);
  return isExclusive(added) ? [added] : next.filter((v) => !isExclusive(v));
}

export type SelectMode = SingleSelection | MultipleSelection;

export type InfiniteScrollMode = {
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
};

export type MultiSelectProps<T extends Item> = {
  label: string;
  options: T[];
  defaultSelectedOptions?: T[];
  allowSearch?: boolean;
  className?: string;
  displayMaxItems?: number;
  displaySelected?: boolean;
  icon?: React.FC<{ className?: string }>;
  iconPosition?: 'left' | 'right';
  isMinWidth?: boolean;
  itemDisplay?: React.FC<{
    item: T;
    active?: boolean;
    selected?: boolean;
  }>;
  onClose?: () => void;
  operator?: string | ReactNode;
  permanent?: boolean;
  placeholder?: string;
  placement?: Placement;
  showOperator?: boolean;
  showSelectAll?: boolean;
  title?: ReactNode;
  trigger?: React.ReactNode;
  virtualize?: boolean;
  disabled?: boolean;
  loading?: boolean;
  error?: unknown;
  setManualQuery?: (query?: string) => void;
  /** A server search is under way: the list says so, not "No results". */
  searching?: boolean;
  displayButtonClassName?: string;
  selectedLabelClassName?: string;
  selectText?: string | ReactNode | null;
  showClear?: boolean;
  showInlineClear?: boolean;
  hideIcon?: boolean;
  virtualizationOptions?: {
    containerStyles?: React.CSSProperties;
    virtuosoStyle: React.CSSProperties;
  };
  dataTestId?: string;
} & SelectMode &
  InfiniteScrollMode;

/**
 * Note to manage width when virtualizing:
 * When virtualize is set to true, you need to set the width, because the dropdown cannot automatically
 * adjust its width to fit the content.
 * To set the width, you need to use virtualizationOptions prop. For example this
 * configuration will set the width to 400px:
 *
 * ```tsx
 *      virtualizationOptions={{
 *         containerStyles: {
 *           maxWidth: 400,
 *         },
 *         virtuosoStyle: {
 *           maxWidth: 400,
 *           width: 400,
 *         },
 *       }}
 * ```
 * Default virtualized width is 300px.
 */
export function MultiSelect<T extends Item>({
  allowSearch = false,
  className,
  displayMaxItems = 3,
  displaySelected = true,
  icon,
  isMinWidth,
  itemDisplay,
  label,
  mode,
  onClose,
  operator,
  options,
  defaultSelectedOptions,
  permanent,
  placeholder = 'Search',
  placement = 'bottom-start',
  setValue,
  showOperator,
  showSelectAll,
  showClear,
  title,
  trigger,
  value,
  virtualize = false,
  setManualQuery,
  searching = false,
  fetchNextPage,
  hasNextPage,
  disabled = false,
  loading,
  error,
  displayButtonClassName,
  selectedLabelClassName,
  selectText,
  showInlineClear,
  hideIcon,
  virtualizationOptions,
  dataTestId,
  iconPosition = 'left',
}: MultiSelectProps<T>) {
  const { t } = useTranslation('designSystem');
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (setManualQuery) {
      setManualQuery(query);
    }
  }, [query, setManualQuery]);

  const filteredOptions =
    query === ''
      ? options
      : options.filter((option) => {
          if (option.permanent) {
            return true;
          }
          const matchesOptionValue = option.value
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, ''));
          if (matchesOptionValue) {
            return true;
          }
          const matchesOptionLabel =
            typeof option.label === 'string' &&
            option.label
              .toLowerCase()
              .replace(/\s+/g, '')
              .includes(query.toLowerCase().replace(/\s+/g, ''));
          if (matchesOptionLabel) {
            return true;
          }
          if (!option.searchString) {
            return false;
          }
          const queryTokens = query.toLowerCase().split(' ').filter(Boolean);
          const optionSearchString = option.searchString
            .toLowerCase()
            .replace(/\s+/g, '');
          return queryTokens.every((queryToken) =>
            optionSearchString.includes(queryToken),
          );
        });

  const { x, y, refs, context } = useFloating({
    placement,
    middleware: [
      offset(5),
      shift({ padding: 8 }),
      // Flip above the trigger when there is no room below, without changing
      // the alignment the caller asked for.
      autoPlacement({
        allowedPlacements: allowedPlacements(placement),
      }),
    ],
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });

  const { getFloatingProps } = useInteractions([
    useHover(context),
    useFocus(context),
    useRole(context, { role: 'menu' }),
    useDismiss(context),
  ]);
  const widthPrefix = isMinWidth ? 'min' : 'max';
  const clearFilter = () => {
    setValue(
      options
        .filter((option) => option.permanent)
        .map((option) => option.value) as string & string[],
    );
    setQuery('');
  };

  return (
    <Combobox
      as="div"
      className={`relative inline-block w-auto flex-grow-0 antialiased ${className}`}
      value={value}
      onChange={(next) => {
        if (next === null) {
          return;
        }
        const picked =
          Array.isArray(next) && Array.isArray(value)
            ? applyExclusive(next, value, options)
            : next;
        // TODO: This is a temporary fix until we remove this component entirely.
        setValue(picked as string & string[]);
      }}
      multiple={mode !== 'single'}
      disabled={disabled}
    >
      {({ open }) => {
        if (!open) {
          allowSearch && query && setQuery('');
        }
        return (
          <>
            {displaySelected && (
              <div ref={refs.setReference}>
                <DisplayButton
                  options={options.filter((option) => !option.permanent)}
                  defaultSelectedOptions={defaultSelectedOptions}
                  value={value}
                  label={label}
                  icon={hideIcon ? undefined : icon}
                  displayMaxItems={displayMaxItems}
                  onClose={onClose}
                  permanent={permanent}
                  operator={operator}
                  showOperator={showOperator}
                  displayButtonClassName={displayButtonClassName}
                  selectedLabelClassName={selectedLabelClassName}
                  selectText={selectText}
                  clearFilter={clearFilter}
                  showInlineClear={showInlineClear}
                  iconPosition={iconPosition}
                  dataTestId={dataTestId}
                />
              </div>
            )}
            {!displaySelected &&
              (trigger ? (
                <>
                  <ComboboxButton
                    as="div"
                    ref={refs.setReference}
                    data-testid={
                      dataTestId ? `${dataTestId}-trigger` : undefined
                    }
                  >
                    {trigger}
                  </ComboboxButton>
                </>
              ) : (
                <ComboboxButton
                  as="div"
                  className="inline-flex"
                  data-testid={dataTestId ? `${dataTestId}-button` : undefined}
                >
                  <button ref={refs.setReference}>{label || t('open')}</button>
                </ComboboxButton>
              ))}
            {open && (
              <div
                data-testid={dataTestId ? `${dataTestId}-dropdown` : undefined}
                className={`${widthPrefix}-w-[450px] origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-800 dark:ring-gray-600`}
                {...getFloatingProps({
                  ref: refs.setFloating,
                  style: {
                    position: 'fixed',
                    top: y ?? '',
                    left: x ?? '',
                    zIndex: 99999999999999,
                  },
                })}
              >
                {title && (
                  <div className="border-b p-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {title}
                  </div>
                )}
                {allowSearch && (
                  <SearchInput
                    dataTestId={
                      dataTestId ? `${dataTestId}-search-input` : undefined
                    }
                    placeholder={placeholder}
                    setQuery={setQuery}
                    query={query}
                  />
                )}

                <ComboboxOptions>
                  {searching && (
                    <div
                      role="status"
                      className="flex items-center justify-center p-2 text-sm text-gray-500 dark:text-gray-400"
                    >
                      {t('loading')}
                    </div>
                  )}
                  {!searching && filteredOptions.length < 1 && !loading && (
                    <div className="flex items-center justify-center p-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t('noResults')}
                    </div>
                  )}

                  {virtualize || (fetchNextPage && hasNextPage) ? (
                    <div
                      className="max-w-[300px]"
                      style={virtualizationOptions?.containerStyles}
                      data-testid={
                        dataTestId ? `${dataTestId}-options` : undefined
                      }
                    >
                      <SkeletonLoader
                        status={
                          loading ? 'loading' : error ? 'error' : 'success'
                        }
                        error={error}
                        repeat={3}
                      >
                        <VirtualOptionList
                          itemDisplay={itemDisplay}
                          options={
                            searching
                              ? []
                              : filteredOptions.filter(
                                  (option) => !option.permanent,
                                )
                          }
                          mode={mode}
                          key={query}
                          fetchNextPage={fetchNextPage}
                          virtualizationOptions={virtualizationOptions}
                          hasNextPage={hasNextPage}
                        />
                      </SkeletonLoader>
                    </div>
                  ) : (
                    <OptionList
                      itemDisplay={itemDisplay}
                      options={
                        searching
                          ? []
                          : filteredOptions.filter(
                              (option) => !option.permanent,
                            )
                      }
                      mode={mode}
                    />
                  )}

                  {showSelectAll && (
                    <SelectAll
                      onSelectAll={() =>
                        setValue(
                          options.map((x) => x.value) as string & string[],
                        )
                      }
                      onSelectFiltered={() => {
                        setValue(
                          filteredOptions.map(
                            (x) => x.value || x.permanent,
                          ) as string[] & string,
                        );
                      }}
                      filtered={!!query}
                      onClear={clearFilter}
                      fetchNextPage={fetchNextPage}
                      hasNextPage={hasNextPage}
                    />
                  )}
                  {showClear && mode === 'single' && (
                    <div className="flex justify-end gap-2 border-t border-gray-200 bg-white p-[4px] dark:border-gray-700 dark:bg-gray-800">
                      <button
                        className="rounded-md p-1 text-xs font-medium text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
                        onClick={clearFilter}
                      >
                        {t('clear')}
                      </button>
                    </div>
                  )}
                </ComboboxOptions>
              </div>
            )}
          </>
        );
      }}
    </Combobox>
  );
}
